import { createSelector } from 'reselect';
import { AppState } from '../../types';
import { AppCacheStatus as Status } from '@fullstory/redux-util/utils';
import { AppCacheData } from './types';
import { SearchieType } from '@fullstory/proto/fs/services/alexa';

// Data selectors
const getSearchieDataMap = (state: AppState) => state.searchies.data;
type SearchieDataMap = ReturnType<typeof getSearchieDataMap>;

export const makeGetSearchieData = (storageId?: string) =>
  createSelector(
    getSearchieDataMap,
    (map: SearchieDataMap): AppCacheData => getSearchieData(map, storageId),
  );

function getSearchieData(map: SearchieDataMap, storageId?: string) {
  const {
    status = Status.Initial,
    ready = false,
    data = null,
  } = storageId ? map[storageId] ?? {} : {};

  return { status, ready, data };
}

// Settings selectors
const getSearchieSettingsMap = (state: AppState) => state.searchies.settings;

const getUserTrendsSettings = createSelector(
  getSearchieSettingsMap,
  settings => settings.userTrends,
);

const getRageClicksSettings = createSelector(
  getSearchieSettingsMap,
  settings => settings.rageClicks,
);

const getErrorClicksSettings = createSelector(
  getSearchieSettingsMap,
  settings => settings.errorClicks,
);

const getDeadClicksSettings = createSelector(
  getSearchieSettingsMap,
  settings => settings.deadClicks,
);

const getMobileDevicesSettings = createSelector(
  getSearchieSettingsMap,
  settings => settings.mobileDevices,
);

// Searchie selectors
export const getUserTrends = createSelector(
  makeGetSearchieData(SearchieType[SearchieType.SEARCHIE_TYPE_DAILY_ACTIVES]),
  getUserTrendsSettings,
  (data, settings) => ({ data, settings }),
);

export const getRageClicks = createSelector(
  makeGetSearchieData(SearchieType[SearchieType.SEARCHIE_TYPE_TOP_RAGE_CLICKS]),
  getRageClicksSettings,
  (data, settings) => ({ data, settings }),
);

export const getErrorClicks = createSelector(
  makeGetSearchieData(SearchieType[SearchieType.SEARCHIE_TYPE_TOP_ERROR_CLICKS]),
  getErrorClicksSettings,
  (data, settings) => ({ data, settings }),
);

export const getDeadClicks = createSelector(
  makeGetSearchieData(SearchieType[SearchieType.SEARCHIE_TYPE_TOP_DEAD_CLICKS]),
  getDeadClicksSettings,
  (data, settings) => ({ data, settings }),
);

export const getMobileDevices = createSelector(
  makeGetSearchieData(SearchieType[SearchieType.SEARCHIE_TYPE_MOBILE_DEVICES]),
  getMobileDevicesSettings,
  (data, settings) => ({ data, settings }),
);

export const getDeviceBreakdown = makeGetSearchieData(
  SearchieType[SearchieType.SEARCHIE_TYPE_DEVICE_BREAKDOWN],
);

export const getBrowserBreakdown = makeGetSearchieData(
  SearchieType[SearchieType.SEARCHIE_TYPE_TRAFFIC_BY_BROWSER],
);

export const getScreenResolutionBreakdown = makeGetSearchieData(
  SearchieType[SearchieType.SEARCHIE_TYPE_SCREEN_RESOLUTIONS],
);

export const getTopReferrers = makeGetSearchieData(
  SearchieType[SearchieType.SEARCHIE_TYPE_TRAFFIC_BY_REFERER_HOST],
);

export const getTopUtmMediums = makeGetSearchieData(
  SearchieType[SearchieType.SEARCHIE_TYPE_TRAFFIC_BY_UTM_MEDIUM],
);

export const getTopUtmCampaigns = makeGetSearchieData(
  SearchieType[SearchieType.SEARCHIE_TYPE_TRAFFIC_BY_UTM_CAMPAIGN],
);

export const getTopUtmSources = makeGetSearchieData(
  SearchieType[SearchieType.SEARCHIE_TYPE_TRAFFIC_BY_UTM_SOURCE],
);

export const getSegmentHealth = makeGetSearchieData(
  SearchieType[SearchieType.SEARCHIE_TYPE_SEGMENT_HEALTH],
);

export const getMobileAppVersions = makeGetSearchieData(
  SearchieType[SearchieType.SEARCHIE_TYPE_MOBILE_APP_VERSIONS],
);

export const getMobileOsVersions = makeGetSearchieData(
  SearchieType[SearchieType.SEARCHIE_TYPE_MOBILE_OS_VERSIONS],
);

export const getTopPages = makeGetSearchieData(SearchieType[SearchieType.SEARCHIE_TYPE_TOP_PAGES]);

export const getSlowPages = makeGetSearchieData(
  SearchieType[SearchieType.SEARCHIE_TYPE_SLOW_PAGES],
);

export const getMostCommonEntryPages = makeGetSearchieData(
  SearchieType[SearchieType.SEARCHIE_TYPE_MOST_COMMON_ENTRY_PAGES],
);

export const getEventFunnel = makeGetSearchieData(
  SearchieType[SearchieType.SEARCHIE_TYPE_EVENT_FUNNEL],
);

export const getMostActiveUsers = makeGetSearchieData(
  SearchieType[SearchieType.SEARCHIE_TYPE_MOST_ACTIVE_USERS],
);

export const getRageGrade = makeGetSearchieData(
  SearchieType[SearchieType.SEARCHIE_TYPE_RAGE_GRADE],
);

export const getUserDetails = (state: AppState): string | null => {
  if (!state.searchies.oneMostActiveUsers.data) {
    return null;
  } else {
    return state.searchies.oneMostActiveUsers.data.UserLastLatLong;
  }
};

export const getURL = (state: AppState, latLong: string): string | null =>
  state.maps.data?.[latLong] ?? null;
